/**
 * Putting a configurator option on the model — the one routing table the
 * shared link, the studio bar and the builder all follow.
 *
 * The shared link has no editor (no Basic engine, no Undo), so it calls the
 * viewer's RPCs directly through `applyOption`. The studio applies through the
 * Basic engine instead, so a click is one Undo step; `optionMark` in
 * `ui/editor/configurator/studioApply.ts` hands the engine the same decision
 * this file makes, and the engine's `putMaterial` routes it the same way.
 */
import type { MaterialKind, RingClientApi } from "../../shared/api-contract";
import { usesDmat } from "../../ui/editor/library/materialRouting";
import type { ConfiguratorOption, OptionKind, OptionPreset, PresetCategory } from "./configurator";

/** The RPCs an option can need. A `ViewerClient` satisfies this. */
export interface OptionClient {
  ring: Pick<
    RingClientApi,
    | "reclassifyGroup"
    | "applyGemPresetToGroup"
    | "applyDmatUrlToGroup"
    | "applyPmatUrlToGroup"
    | "applyMetalPresetToGroup"
    | "applyMaterialPresetToGroup"
    | "setGroupMetalColor"
  >;
}

/** Is this option a diamond-shader file (a .dmat, or a .pmat known to declare one)? */
export function isDmatOption(option: ConfiguratorOption): boolean {
  const p = option.preset;
  if (!p?.url) return false;
  return p.format ? p.format === "dmat" : usesDmat(p.url, p.kind);
}

/**
 * The kind a group is left as once `option` is on it. A file decides by its
 * own format; a built-in stone is a gem; every other built-in (a named metal, a
 * ceramic, a pearl, an enamel) is a PBR surface, which the viewer files as
 * metal. Legacy colour options never changed a part's kind and still don't.
 */
export function kindAfter(option: ConfiguratorOption, currentKind: MaterialKind): MaterialKind {
  const p = option.preset;
  if (!p || (!p.url && !p.presetId)) return currentKind;
  if (p.url) return isDmatOption(option) ? "gem" : "metal";
  return p.kind === "gem" ? "gem" : "metal";
}

/**
 * Apply one option to one viewer group. Returns the kind the group is now, so
 * the caller can pass it back in as `currentKind` next time.
 *
 * `slotKind` is what the part IS (`slotKindOf`), where `currentKind` is what it
 * is wearing. A legacy colour is a metal's: on a stone slot it is skipped
 * rather than painted onto the stones.
 *
 * Mirrors the studio engine's `putMaterial`:
 * - the group is reclassified FIRST when the material changes what it is;
 * - a gem FILE never lands on a part that is not already a stone: on any other
 *   part the viewer would build a PBR transmission stand-in — glass pretending
 *   to be a diamond — so the part gets the real diamond shader (a clear
 *   diamond) before the file's uniforms go on it.
 */
export async function applyOption(
  client: OptionClient,
  groupId: string,
  option: ConfiguratorOption,
  currentKind: MaterialKind,
  slotKind?: OptionKind,
): Promise<MaterialKind> {
  const r = client.ring;
  const p = option.preset;
  if (!p || (!p.url && !p.presetId)) {
    // Options authored before the library existed: what they did then — except
    // a metal colour on a part that is a stone.
    if (option.metalColor && slotKind !== "gem") await r.setGroupMetalColor(groupId, option.metalColor);
    if (option.gemPresetId) await r.applyGemPresetToGroup(groupId, option.gemPresetId);
    return currentKind;
  }
  const kind = kindAfter(option, currentKind);
  if (kind !== currentKind) await r.reclassifyGroup(groupId, kind);
  if (p.url) {
    if (kind === "gem") {
      if (currentKind !== "gem") await r.applyGemPresetToGroup(groupId, "diamondClear");
      await r.applyDmatUrlToGroup(groupId, p.url);
    } else {
      await r.applyPmatUrlToGroup(groupId, p.url);
    }
  } else if (p.presetId) {
    if (p.kind === "metal") await r.applyMetalPresetToGroup(groupId, p.presetId, p.finishId ?? "polished");
    else if (p.kind === "gem") await r.applyGemPresetToGroup(groupId, p.presetId);
    // Ceramic / pearl / enamel ids exist only in the material library.
    else await r.applyMaterialPresetToGroup(groupId, p.presetId);
  }
  return kind;
}

/**
 * An absolute URL for an asset a shared link will load.
 *
 * A shopper opens the link on a different page, at a different time, so a
 * relative path would resolve against whatever document happens to fetch it.
 * Material files are fetched by the VIEWER, so pass the viewer's origin for
 * those; a thumbnail is shown by the host page, so pass the page's.
 * `data:` URLs (a workspace's own material file) are already self-contained.
 */
export function resolveAssetUrl(url: string, base: string): string {
  const u = url.trim();
  if (!u || /^(data|blob):/i.test(u)) return u;
  try {
    return new URL(u, base.endsWith("/") ? base : `${base}/`).href;
  } catch {
    return u;
  }
}

/** What the studio's material library hands a picker (MaterialCategoryGrid's `LibMaterial`). */
export interface LibraryMaterial {
  id: string;
  label: string;
  category: PresetCategory;
  fileUrl?: string;
  thumbUrl?: string;
  color?: number;
  format?: "pmat" | "dmat";
}

/** A swatch colour for a material with none of its own, per category. */
const DEFAULT_SWATCH: Record<PresetCategory, string> = {
  metal: "#c9c9c6",
  gem: "#eef0f2",
  ceramic: "#c9c9c6",
  pearl: "#f3efe6",
  other: "#c9c9c6",
};

const hex = (n: number) => `#${Math.max(0, Math.min(0xffffff, Math.round(n))).toString(16).padStart(6, "0")}`;

/** The option id a library item becomes — the same item twice is one option. */
export const libraryOptionId = (itemId: string) => `lib:${itemId}`;

/** The library item an option came from, if it came from the library. */
export const libraryItemId = (optionId: string): string | null =>
  optionId.startsWith("lib:") ? optionId.slice(4) : null;

/**
 * A configurator option for a library material, with every URL made absolute.
 * A named metal goes in polished — the finish a shopper expects to see.
 */
export function optionFromLibrary(
  item: LibraryMaterial,
  origins: { viewer: string; page: string },
): ConfiguratorOption {
  const preset: OptionPreset = item.fileUrl
    ? { kind: item.category, url: resolveAssetUrl(item.fileUrl, origins.viewer), ...(item.format ? { format: item.format } : {}) }
    : { kind: item.category, presetId: item.id, ...(item.category === "metal" ? { finishId: "polished" } : {}) };
  return {
    id: libraryOptionId(item.id),
    name: item.label,
    swatch: item.color != null ? hex(item.color) : DEFAULT_SWATCH[item.category],
    preset,
    ...(item.thumbUrl ? { thumbUrl: resolveAssetUrl(item.thumbUrl, origins.page) } : {}),
  };
}
